import { useEffect, useRef, useState } from "react";
import { fetchFlightsByPlacement, logImpression, logClick } from "../../lib/ads";

type Creative = {
  id: string | number;
  title?: string | null;
  img_url: string;
  click_url?: string | null;
  width?: number | null;
  height?: number | null;
};

type Flight = {
  id: string | number;
  placement_code: string;
  ad_creatives: Creative[];
};

type Props = {
  code: "top-banner" | "footer";
  /** 원본 비율(PC 1140x120 → 9.5, Mobile 320x80 → 4) */
  aspect: number;
  /** 보이는 높이(px)를 강제 – dev/prod 동일 크기 유지 */
  forceHeight?: number;
  className?: string;
};

/** DB 실패 시에도 노출되는 안전한 폴백 이미지 */
const FALLBACK: Record<"top-banner" | "footer", { pc: string; mobile: string }> = {
  "top-banner": {
    pc: "https://auth.a-blind.com/storage/v1/object/public/ads/pc/top/top_pc_1140x120.webp",
    mobile: "https://auth.a-blind.com/storage/v1/object/public/ads/mobile/top/top_m_320x80.webp",
  },
  footer: {
    pc: "https://auth.a-blind.com/storage/v1/object/public/ads/pc/footer/footer_pc_1140x120.webp",
    mobile: "https://auth.a-blind.com/storage/v1/object/public/ads/mobile/footer/footer_m_320x80.webp",
  },
};

export default function AdSlot({ code, aspect, forceHeight = 0, className }: Props) {
  const [flight, setFlight] = useState<Flight | null>(null);
  const [creative, setCreative] = useState<Creative | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  // 1) 광고 불러오기 (프로덕션에서도 실패 시 폴백)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await fetchFlightsByPlacement(code);
        if (!alive) return;
        if (rows?.length && rows[0]?.ad_creatives?.length) {
          setFlight(rows[0] as Flight);
          setCreative(rows[0].ad_creatives[0] as Creative);
        } else {
          setFlight(null);
          setCreative(null);
        }
      } catch {
        setFlight(null);
        setCreative(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [code]);

  // 2) 노출 로그 (IntersectionObserver)
  useEffect(() => {
    if (!shellRef.current) return;
    let seen = false;
    const io = new IntersectionObserver(
      (ents) => {
        if (!seen && ents.some((e) => e.isIntersecting)) {
          seen = true;
          if (flight?.id != null) logImpression(String(flight.id));
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(shellRef.current);
    return () => io.disconnect();
  }, [flight?.id]);

  // 3) 렌더 크기 계산 (항상 공간 확보)
  const pct = 100 / aspect; // aspect = width/height → height% = 100 / aspect
  const isMobile = typeof window !== "undefined" ? window.innerWidth < 768 : false;

  const frameStyle: React.CSSProperties = {
    // Tailwind 임의 값 purge 이슈 방지를 위해 중요한 건 인라인로
    border: "1px solid rgba(0,0,0,0.06)",
    borderRadius: 10,
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(2px)",
    padding: 8,
    width: "100%",
  };

  const boxStyle: React.CSSProperties = forceHeight
    ? {
        height: forceHeight,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }
    : {
        position: "relative",
        width: "100%",
        paddingTop: `${pct}%`, // 비율 상자
      };

  const imgStyle: React.CSSProperties = forceHeight
    ? { height: "100%", width: "auto", maxWidth: "100%", objectFit: "contain", display: "block" }
    : { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" };

  const Img = (src: string, alt: string) => <img src={src} alt={alt} style={imgStyle} loading="lazy" />;

  // 4) 실제 렌더 (항상 프레임을 그리고, 없으면 폴백)
  const src =
    creative?.img_url ||
    (isMobile ? FALLBACK[code].mobile : FALLBACK[code].pc); // DB 실패/공백일 때도 보여줌

  const body = creative?.click_url ? (
    <a
      href={creative.click_url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        if (flight?.id != null) logClick(String(flight.id));
      }}
      aria-label={creative.title || code}
    >
      <div style={boxStyle}>{Img(src, creative.title || code)}</div>
    </a>
  ) : (
    <div style={boxStyle}>{Img(src, creative?.title || code)}</div>
  );

  return (
    <div ref={shellRef} className={className} style={frameStyle}>
      {body}
    </div>
  );
}
